import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material'
import { IKoreanSchoolYearlyStatistic } from 'Entities/koreanSchoolYearlyStatistics'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useKoreanSchoolYearlyStatistics } from '../Hooks/useKoreanSchoolYearlyStatistics'

type Props = {
  open: boolean
  onClose: () => void
  item: IKoreanSchoolYearlyStatistic
}

export const KoreanSchoolYearlyEditDialog = ({ open, onClose, item }: Props) => {
  const { updateMutation } = useKoreanSchoolYearlyStatistics()
  const [form, setForm] = useState({
    year: String(item.year),
    schoolCount: String(item.schoolCount),
    universityCount: String(item.universityCount),
  })
  const [touched, setTouched] = useState({
    year: false,
    schoolCount: false,
    universityCount: false,
  })

  const rawErrors = useMemo(
    () => ({
      year: !form.year.trim() || Number(form.year) <= 0,
      schoolCount: !form.schoolCount.trim() || Number(form.schoolCount) < 0,
      universityCount: !form.universityCount.trim() || Number(form.universityCount) < 0,
    }),
    [form],
  )

  const handleChange = (field: 'year' | 'schoolCount' | 'universityCount') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    if (/^\d*$/.test(value)) {
      setForm(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleBlur = (field: 'year' | 'schoolCount' | 'universityCount') => () => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const hasNoChanges =
    Number(form.year) === item.year &&
    Number(form.schoolCount) === item.schoolCount &&
    Number(form.universityCount) === item.universityCount

  const hasErrors = rawErrors.year || rawErrors.schoolCount || rawErrors.universityCount

  const onSave = () => {
    if (hasErrors) {
      setTouched({ year: true, schoolCount: true, universityCount: true })
      return
    }
    if (hasNoChanges) {
      toast.warning('Ничего не изменилось!')
      return
    }

    updateMutation.mutate(
      {
        id: item.id,
        payload: {
          year: Number(form.year),
          schoolCount: Number(form.schoolCount),
          universityCount: Number(form.universityCount),
        },
      },
      {
        onSuccess: () => onClose(),
      },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>Изменить годовую статистику</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Год'
              value={form.year}
              onChange={handleChange('year')}
              onBlur={handleBlur('year')}
              error={rawErrors.year && touched.year}
              helperText={rawErrors.year && touched.year ? 'Введите корректный год.' : ' '}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Количество школ'
              value={form.schoolCount}
              onChange={handleChange('schoolCount')}
              onBlur={handleBlur('schoolCount')}
              error={rawErrors.schoolCount && touched.schoolCount}
              helperText={rawErrors.schoolCount && touched.schoolCount ? 'Введите корректное число.' : ' '}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Количество университетов'
              value={form.universityCount}
              onChange={handleChange('universityCount')}
              onBlur={handleBlur('universityCount')}
              error={rawErrors.universityCount && touched.universityCount}
              helperText={rawErrors.universityCount && touched.universityCount ? 'Введите корректное число.' : ' '}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='inherit'>
          Отмена
        </Button>
        <Button onClick={onSave} variant='contained' disabled={updateMutation.isLoading}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  )
}
